// ── Visual Report Schema ─────────────────────────────────────────
// Composable block types for schema-driven tool card rendering.
// No React imports — pure types + runtime guard.

export interface StatusBadgeBlock {
    type: "status_badges";
    badges: {label: string; count: number; variant: "success" | "warning" | "danger"}[];
}

export interface StatusListItem {
    label: string;
    status: string;
    detail?: string;
}

export interface StatusListBlock {
    type: "status_list";
    items: StatusListItem[];
    max_visible?: number;
}

export interface MetricGridItem {
    label: string;
    value: string;
    detail?: string;
}

export interface MetricGridBlock {
    type: "metric_grid";
    items: MetricGridItem[];
    columns?: number;
}

export interface ComparisonTableColumn {
    key: string;
    label: string;
    align?: "left" | "right";
}

export interface ComparisonTableRow {
    values: Record<string, string>;
    highlight?: "positive" | "negative" | "neutral";
}

export interface ComparisonTableBlock {
    type: "comparison_table";
    columns: ComparisonTableColumn[];
    rows: ComparisonTableRow[];
    caption?: string;
}

export interface KeyValueItem {
    label: string;
    value: string;
    detail?: string;
    highlight?: "positive" | "negative" | "neutral";
}

export interface KeyValueListBlock {
    type: "kv_list";
    heading?: string;
    items: KeyValueItem[];
}

export interface NarrativeBlock {
    type: "narrative";
    body: string;
}

export interface ChartSeries {
    key: string;
    label: string;
    color?: string;
    type?: "line" | "bar";
    stack_id?: string;
    dash?: boolean;
}

// Base fields shared by all chart blocks
interface ChartBlockBase {
    type: "chart";
    height?: number;
    show_legend?: boolean;
}

// Recharts path (existing, backwards compatible)
export interface RechartsChartBlock extends ChartBlockBase {
    chart_engine?: "recharts"; // default when omitted
    chart_type: "line" | "bar" | "composed";
    x_key: string;
    series: ChartSeries[];
    data: Record<string, unknown>[];
    y_format?: "currency" | "number" | "percent";
    forecast_boundary?: string;
}

// ECharts path (raw option passthrough)
export interface EChartsChartBlock extends ChartBlockBase {
    chart_engine: "echarts";
    chart_type: "treemap" | "pie" | "line" | "bar" | "composed";
    echarts_option: Record<string, unknown>;
}

// ECharts builder path (dispatches to echarts-toolkit builder functions)
export interface EChartsBuilderBlock extends ChartBlockBase {
    chart_engine: "echarts_builder";
    builder: "line" | "area" | "bar" | "stacked_bar" | "combo" | "waterfall"
           | "pie" | "donut" | "heatmap" | "scatter" | "radar" | "treemap";
    builder_config: Record<string, unknown>;
}

export type ChartBlock = RechartsChartBlock | EChartsChartBlock | EChartsBuilderBlock;

// ── KPI Row ──────────────────────────────────────────────────────

export interface KpiRowItem {
    label: string;
    value: string;
    sub_text?: string;
    sub_color?: "red" | "green" | "amber" | "neutral";
    highlight?: boolean;
}

export interface KpiRowBlock {
    type: "kpi_row";
    items: KpiRowItem[];
}

// ── Section Header ───────────────────────────────────────────────

export interface SectionHeaderBlock {
    type: "section_header";
    title: string;
    subtitle?: string;
}

// ── Bridge Table ─────────────────────────────────────────────────

export interface BridgeTableRow {
    values: Record<string, string>;
    row_type?: "data" | "section_header" | "subtotal" | "total";
    highlight?: "favourable" | "unfavourable" | "flagged" | "neutral";
    commentary?: string;
}

export interface BridgeTableBlock {
    type: "bridge_table";
    columns: ComparisonTableColumn[];
    rows: BridgeTableRow[];
    caption?: string;
    sticky_header?: boolean;
}

// ── Callout ──────────────────────────────────────────────────────

export interface CalloutBlock {
    type: "callout";
    variant: "info" | "warning" | "critical" | "success";
    title: string;
    body: string;
    detail?: string;
}

// ── Data Table ───────────────────────────────────────────────────

export interface DataTableColumn {
    key: string;
    label: string;
    align?: "left" | "right" | "center";
    format?: "currency" | "percent" | "number" | "text";
}

export interface DataTableRow {
    values: Record<string, string | number>;
    group?: string;
    highlight_row?: boolean;
}

export interface DataTableBlock {
    type: "data_table";
    columns: DataTableColumn[];
    rows: DataTableRow[];
    max_height?: number;
    filter_key?: string;
    caption?: string;
    sortable?: boolean;
}

// ── Matrix Grid ──────────────────────────────────────────────────

export interface MatrixCell {
    value: string;
    detail?: string;
    intensity?: number;
    color?: "green" | "amber" | "red" | "blue";
}

export interface MatrixGridBlock {
    type: "matrix_grid";
    row_headers: string[];
    col_headers: string[];
    cells: (MatrixCell | null)[][];
    title?: string;
    description?: string;
}

// ── Union & Validation ───────────────────────────────────────────

export type ReportBlock =
    | StatusBadgeBlock
    | StatusListBlock
    | MetricGridBlock
    | ComparisonTableBlock
    | KeyValueListBlock
    | NarrativeBlock
    | ChartBlock
    | KpiRowBlock
    | SectionHeaderBlock
    | BridgeTableBlock
    | CalloutBlock
    | DataTableBlock
    | MatrixGridBlock;

export interface VisualReport {
    report_type: string;
    title: string;
    subtitle?: string;
    blocks: ReportBlock[];
}

const VALID_BLOCK_TYPES = new Set([
    "status_badges",
    "status_list",
    "metric_grid",
    "comparison_table",
    "kv_list",
    "narrative",
    "chart",
    "kpi_row",
    "section_header",
    "bridge_table",
    "callout",
    "data_table",
    "matrix_grid",
]);

export function isValidVisualReport(obj: unknown): obj is VisualReport {
    if (!obj || typeof obj !== "object") return false;
    const o = obj as Record<string, unknown>;
    if (typeof o.report_type !== "string" || !o.report_type) return false;
    if (typeof o.title !== "string" || !o.title) return false;
    if (!Array.isArray(o.blocks)) return false;
    for (const block of o.blocks) {
        if (!block || typeof block !== "object") return false;
        const b = block as Record<string, unknown>;
        if (typeof b.type !== "string" || !VALID_BLOCK_TYPES.has(b.type)) return false;
    }
    return true;
}
